import { UPLOAD_LIMITS } from './uploader.constants';

// Record over the rule's own union: a new photo extension in UPLOAD_LIMITS fails the build until it gets a MIME type.
const PHOTO_MIME_BY_EXTENSION: Record<(typeof UPLOAD_LIMITS.photoExtensions)[number], string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  png: 'image/png',
  heic: 'image/heic',
  heif: 'image/heif',
};

const PHOTO_MIME_TYPES = [...new Set(UPLOAD_LIMITS.photoExtensions.map(ext => PHOTO_MIME_BY_EXTENSION[ext]))];
const PHOTO_EXTENSIONS = UPLOAD_LIMITS.photoExtensions.map(ext => `.${ext}`);

/**
 * Строка для атрибута accept в input[type=file]
 * Формат: "image/jpeg,image/webp,image/png,image/heic,image/heif,.jpg,.jpeg,.webp,.png,.heic,.heif"
 */
export const ACCEPTED_IMAGE_TYPES_STRING: string = [...PHOTO_MIME_TYPES, ...PHOTO_EXTENSIONS].join(',');

export function isImageFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return PHOTO_MIME_TYPES.includes(file.type.toLowerCase()) || PHOTO_EXTENSIONS.some(ext => name.endsWith(ext));
}

/** Transparent 1x1: the tile under it shows through. The fragment keeps each URL unique for `track`. */
const NO_LOCAL_PREVIEW = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
let noPreviewSeq = 0;

/** A blob URL of the picked file for an `<img>`; a HEIC gets a transparent stand-in instead of a broken image. */
export function localPreviewUrl(file: File): string {
  return isHeicFile(file) ? `${NO_LOCAL_PREVIEW}#${++noPreviewSeq}` : URL.createObjectURL(file);
}

/**
 * HEIC/HEIF (iPhone). The server decodes it and cuts JPEG versions; outside Safari the browser cannot draw the
 * picked file, so a HEIC never gets a local blob preview. Extension counts as much as type: some browsers send
 * an empty type for .heic.
 */
const HEIC_MIME_TYPES = ['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence'];
const HEIC_EXTENSIONS = ['.heic', '.heif'];

export function isHeicFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return HEIC_MIME_TYPES.includes(file.type.toLowerCase()) || HEIC_EXTENSIONS.some(ext => name.endsWith(ext));
}

/**
 * Естественный порядок имён: IMG_2 идёт перед IMG_10, а не после. Пачка встаёт в очередь
 * в том же порядке, в каком фотограф видит её в папке, — плитки в полёте выстраиваются так же.
 */
export function sortFilesNaturally(files: readonly File[]): File[] {
  return [...files].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }));
}
